import * as React from 'react';
import styled from 'styled-components';

import { Slot } from '../../internals/Slot';
import { getSemanticAttributes } from '../../internals/semanticProps';
import { Spinner } from '../Spinner';
import { BUTTON_BORDER_WIDTH, buttonGeometryCss } from './buttonGeometry';
import type { ButtonLayoutMode } from './buttonGeometry';
import type { ButtonSize, ButtonVariant } from './buttonTypes';
import { getButtonVariantRecipe } from './buttonVariantRecipes';

export type { ButtonSize, ButtonVariant } from './buttonTypes';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: string | React.ReactNode;
  trailingIcon?: string | React.ReactNode;
  /** Preferred label slot (avoids confusion with native `title` tooltip). */
  label?: string | React.ReactNode;
  /** @deprecated Prefer `label` or `children` for visible button text. */
  title?: string | React.ReactNode;
  asChild?: boolean;
  semanticId?: string;
  selected?: boolean;
  fontColor?: string;
  borderColor?: string;
}

function layoutModeForVariant(variant: ButtonVariant): ButtonLayoutMode {
  switch (variant) {
    case 'link':
      return 'link';
    case 'ghost':
      return 'ghost';
    case 'rounded-icon-secondary':
    case 'rounded-icon-secondary-dark':
    case 'small-icon-dark':
      return 'icon-only';
    default:
      return 'default';
  }
}

const transientProps = ['$variant', '$size', '$selected', '$fontColor', '$borderColor'] as const;

const shouldForwardTransient = (prop: string | number): boolean =>
  !transientProps.includes(prop as typeof transientProps[number]);

const StyledButton = styled.button.withConfig({ shouldForwardProp: shouldForwardTransient })<{
  $variant: ButtonVariant;
  $size: ButtonSize;
  $selected?: boolean;
  $fontColor?: string;
  $borderColor?: string;
}>`
  box-sizing: border-box;
  font-family: var(--ui-font-sans);
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  position: relative;
  white-space: nowrap;
  cursor: pointer;
  background: none;
  background-position: center;
  transition:
    background 0.2s ease-in-out,
    color 0.2s ease-in-out,
    border-color 0.2s ease-in-out,
    box-shadow 0.2s ease-in-out;

  border-width: ${BUTTON_BORDER_WIDTH};
  border-style: solid;
  border-color: transparent;

  ${({ $size, $variant }) => buttonGeometryCss($size, layoutModeForVariant($variant))}

  &:disabled {
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid var(--ui-focus-ring);
    outline-offset: 2px;
  }

  ${({ $variant, $selected, $fontColor, $borderColor }) =>
    getButtonVariantRecipe($variant, {
      selected: $selected,
      fontColor: $fontColor,
      borderColor: $borderColor,
    })}
`;

const StyledButtonSlot = styled(Slot).withConfig({ shouldForwardProp: shouldForwardTransient })<{
  $variant: ButtonVariant;
  $size: ButtonSize;
  $selected?: boolean;
  $fontColor?: string;
  $borderColor?: string;
}>`
  ${StyledButton}
`;

function renderIcon(icon: string | React.ReactNode): React.ReactNode {
  if (typeof icon === 'string') {
    return <span className="material-symbols-outlined">{icon}</span>;
  }
  return icon;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    type = 'button',
    variant = 'primary',
    size = 'md',
    loading = false,
    icon,
    trailingIcon,
    label,
    title,
    asChild = false,
    semanticId,
    disabled,
    children,
    selected,
    fontColor,
    borderColor,
    ...rest
  },
  ref,
) {
  const semanticProps = getSemanticAttributes(semanticId);
  const isDisabled = Boolean(disabled || loading);
  const dataState = loading ? 'loading' : isDisabled ? 'disabled' : 'idle';
  const layout = layoutModeForVariant(variant);

  const visibleLabel = label ?? title ?? children;

  if (
    process.env.NODE_ENV !== 'production' &&
    layout === 'icon-only' &&
    rest['aria-label'] == null &&
    rest['aria-labelledby'] == null
  ) {
    console.warn(
      'Button: icon-only variants should set aria-label or aria-labelledby for screen readers.',
    );
  }

  const content = (
    <>
      {icon != null && renderIcon(icon)}
      {loading ? <Spinner size="sm" /> : visibleLabel}
      {trailingIcon != null && renderIcon(trailingIcon)}
    </>
  );

  if (asChild) {
    if (!React.isValidElement(children)) {
      return null;
    }

    return (
      <StyledButtonSlot
        ref={ref as React.ForwardedRef<HTMLElement>}
        $variant={variant}
        $size={size}
        $selected={selected}
        $fontColor={fontColor}
        $borderColor={borderColor}
        data-state={dataState}
        data-size={size}
        data-layout={layout}
        data-disabled={isDisabled || undefined}
        aria-busy={loading}
        {...semanticProps}
        {...(rest as React.HTMLAttributes<HTMLElement>)}
      >
        {children}
      </StyledButtonSlot>
    );
  }

  return (
    <StyledButton
      ref={ref}
      type={type}
      $variant={variant}
      $size={size}
      $selected={selected}
      $fontColor={fontColor}
      $borderColor={borderColor}
      data-state={dataState}
      data-size={size}
      data-layout={layout}
      data-disabled={isDisabled || undefined}
      disabled={isDisabled}
      aria-busy={loading}
      {...semanticProps}
      {...rest}
    >
      {content}
    </StyledButton>
  );
});

Button.displayName = 'Button';
